// pages/Login.tsx - GÜNCELLENMİŞ HALİ
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  alpha,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  LockOutlined,
  Email,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: ''
  });

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sayfa yüklendiğinde kayıtlı kullanıcı bilgilerini kontrol et
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Component unmount olduğunda request'i iptal et
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Form validation fonksiyonu
  const validateForm = () => {
    const errors = {
      email: '',
      password: ''
    };

    let isValid = true;

    // Email validation
    if (!email) {
      errors.email = 'Email adresi zorunludur';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Geçerli bir email adresi giriniz';
      isValid = false;
    }

    // Password validation (sadece boş kontrolü)
    if (!password) {
      errors.password = 'Şifre zorunludur';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Real-time validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (formErrors.email) {
      setFormErrors(prev => ({ ...prev, email: '' }));
    }
    if (error) setError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (formErrors.password) {
      setFormErrors(prev => ({ ...prev, password: '' }));
    }
    if (error) setError('');
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
    
    // Eğer "Beni Hatırla" işaretini kaldırırsa, kayıtlı email'i de temizle
    if (!isChecked) {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberMe');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Form validation
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Önceki request varsa iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Yeni abort controller oluştur
    abortControllerRef.current = new AbortController();

    try {
      const success = await login(email, password, abortControllerRef.current.signal);
      
      if (success) {
        // "Beni Hatırla" seçeneği işaretliyse email'i localStorage'a kaydet
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
          localStorage.setItem('rememberMe', 'true');
        } else {
          // İşaretli değilse kayıtlı verileri temizle
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberMe');
        }
        
        navigate('/');
      } else {
        setError('Geçersiz email veya şifre');
      }
    } catch (err: any) {
      // AbortError ise normal, diğer hataları göster
      if (err.name === 'AbortError') {
        console.log('Login request cancelled');
      } else if (err.message === 'Login timeout') {
        setError('Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.');
      } else {
        setError(err.response?.data?.error || 'Giriş sırasında hata oluştu');
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  if (authLoading || isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #F8F4EB 0%, #F2D9BA 50%, #E8C9A1 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#154375', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#154375' }}>
            {isAuthenticated ? 'Yönlendiriliyor...' : 'Yükleniyor...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8F4EB 0%, #ebebeb 50%, #ebebeb 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 10% 20%, rgba(21, 67, 117, 0.05) 0%, transparent 40%),
            radial-gradient(circle at 90% 80%, rgba(242, 217, 186, 0.1) 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, rgba(248, 244, 235, 0.2) 0%, transparent 60%)
          `,
        },
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Login Card */}
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 9,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.4)',
              boxShadow: `
                0 12px 36px rgba(21, 67, 117, 0.15),
                inset 0 1px 0 #000000
              `,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 7,
                background: 'linear-gradient(135deg, #0d2e52 0%, #154375 100%)',
              },
            }}
          >
            {/* Form Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0d2e52 0%, #154375 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <LockOutlined sx={{ fontSize: 32, color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
              </Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #0d2e52 0%, #154375 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1,
                }}
              >
                Hoş Geldiniz
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#5D6D7E',
                  fontSize: '1.05rem',
                  fontWeight: 500,
                }}
              >
                Devam etmek için hesabınıza giriş yapın
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid #d32f2f',
                  background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.05) 0%, rgba(211, 47, 47, 0.02) 100%)',
                  fontWeight: 500,
                  color: '#154375',
                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.1)',
                }}
              >
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                label="Email Adresi"
                type="email"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: formErrors.email ? '#d32f2f' : '#154375' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    '&:hover fieldset': {
                      borderColor: formErrors.email ? '#d32f2f' : '#154375',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: formErrors.email ? '#d32f2f' : '#154375',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: formErrors.email ? '#d32f2f' : '#154375',
                    fontWeight: 600,
                  },
                  '& .MuiFormHelperText-root': {
                    fontWeight: 500,
                    marginLeft: 0,
                    marginTop: 1,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Şifre"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                disabled={loading}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: formErrors.password ? '#d32f2f' : '#154375' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        disabled={loading}
                        sx={{ 
                          color: formErrors.password ? '#d32f2f' : '#7B7D7D',
                          '&:hover': { 
                            color: formErrors.password ? '#d32f2f' : '#154375',
                            background: alpha(formErrors.password ? '#d32f2f' : '#154375', 0.1)
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    '&:hover fieldset': {
                      borderColor: formErrors.password ? '#d32f2f' : '#154375',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: formErrors.password ? '#d32f2f' : '#154375',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: formErrors.password ? '#d32f2f' : '#154375',
                    fontWeight: 600,
                  },
                  '& .MuiFormHelperText-root': {
                    fontWeight: 500,
                    marginLeft: 0,
                    marginTop: 1,
                  },
                }}
              />

              {/* Beni Hatırla Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    disabled={loading}
                    sx={{
                      color: '#154375',
                      '&.Mui-checked': {
                        color: '#154375',
                      },
                      '&.Mui-disabled': {
                        color: '#BFC9CA',
                    },
                    marginLeft: '4px',
                    }}
                  />
                }
                label={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: rememberMe ? '#154375' : '#5D6D7E',
                      fontWeight: rememberMe ? 600 : 400,
                    }}
                  >
                    Beni Hatırla
                  </Typography>
                }
                sx={{
                  mb: 2,
                  '&:hover': {
                    '& .MuiTypography-root': {
                      color: '#154375',
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: '1.05rem',
                  background: 'linear-gradient(135deg, #0d2e52 0%, #154375 50%, #1a5399 100%)',
                  textTransform: 'none',
                  letterSpacing: 0.5,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    background: 'linear-gradient(135deg, #1a5399 0%, #154375 50%, #0d2e52 100%)',
                  },
                  '&:disabled': {
                    background: '#BFC9CA',
                    transform: 'none',
                    boxShadow: 'none',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Giriş Yap'
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;